import React from 'react';
import {
  Box, Snackbar, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import '../../slick-custom.css';
import { useDevSettings } from '../../../contexts/DevSettingsContext';
import { useHubPetParent } from '../HubPetParentContext';
import HubSectionContainer from '../CustomContainer';
import CustomCarouselContainer from './CustomCarouselContainer';
import CtaCard from './CtaCard';
import TrainingPlan from './Checklist/TrainingPlan';
import NextUpCard from './Checklist/NextUpCard';
import MembershipUpsell from './Checklist/MembershipUpsell';
import TrainerContactBanner from './Checklist/TrainerContactBanner';
import { CAT_CHECKLIST, DOG_CHECKLIST } from './checklistData';
import {
  fillPet, getPetAgeBracket, getRegion,
} from './checklistUtils';
import { getPrimaryAnimal, getQuickActionCards } from './utils';
import PreSurrenderHome from '../PreSurrender';
import getPreSurrenderAnimal from '../PreSurrender/eligibility';
import FosterHome from '../Foster';
import getHubAudience, { getFosterAnimal } from '../hubAudience';
import useHubPageView from '../useHubPageView';
import useChecklist from './useChecklist';

const COMPLETE_TITLE = 'Congratulations on Completing Your 30-Day Checklist! 🎉';
const COMPLETE_BODY = "You've built a strong foundation. Continue receiving trainer support and personalized "
  + 'guidance by upgrading your membership.';

function AdopterHome() {
  useHubPageView();
  const navigate = useNavigate();
  const { enhancePetParentData } = useDevSettings();
  const { petParent: realData, loading } = useHubPetParent();
  const petParent = enhancePetParentData(realData);

  const primaryAnimal = getPrimaryAnimal(petParent);
  const isDog = primaryAnimal?.animalType?.toLowerCase() === 'dog';
  // Deliberately species-generic, never the animal's stored name — shelter data
  // often has wrong/placeholder names (e.g. "Cat No Records", case numbers).
  const petName = isDog ? 'your dog' : 'your cat';
  const checklist = React.useMemo(
    () => fillPet(isDog ? DOG_CHECKLIST : CAT_CHECKLIST, petName),
    [isDog, petName],
  );
  const ageBracket = getPetAgeBracket(primaryAnimal?.birthDate);
  const region = getRegion(petParent?.awo?.country);
  const expired = petParent?.membershipStatus === 'expired';

  const {
    checked, expanded, stats, done, total, percent, nextTask,
    onCheck, onExpand, goToTask, registerTaskRef, toast, clearToast,
  } = useChecklist(checklist, { petParent, loading });

  // Cards are gated on real data — the dev-settings enhancer injects a mock pet
  // with fake medical records, which would force the records card on in staging.
  const ctaCards = React.useMemo(
    () => getQuickActionCards({ petParent: realData, navigate }),
    [realData, navigate],
  );

  if (loading) return null;

  const orgName = petParent?.awo?.displayName ?? petParent?.awo?.name;

  let hero;
  if (expired) {
    hero = <MembershipUpsell />;
  } else if (nextTask) {
    hero = <NextUpCard task={nextTask} percent={percent} done={done} total={total} onGo={goToTask} />;
  } else {
    hero = <MembershipUpsell title={COMPLETE_TITLE} body={COMPLETE_BODY} />;
  }

  return (
    <Box>
      <Box sx={{ borderBottom: '1px solid #E0E0E0' }}>
        <HubSectionContainer wide sx={{ gap: 0.5, py: 3 }}>
          <Typography variant="sectionTitle">{`Hi, ${petParent?.firstName}`}</Typography>
          <Typography variant="body2">
            {orgName
              ? `Congratulations on adopting from ${orgName}! We're here with resources and support — anytime `
                + 'you need us.'
              : "We're here with resources and support for you and your pet — anytime you need us."}
          </Typography>
        </HubSectionContainer>
      </Box>
      <HubSectionContainer backgroundContrast wide sx={{ py: 3 }}>
        {hero}
      </HubSectionContainer>
      {!expired && (
        // Small bottom padding: the carousel already carries spacing below the cards
        // (card margin + arrow band when paging is needed).
        <HubSectionContainer backgroundContrast wide sx={{ gap: 3, pb: 1 }}>
          <Typography variant="h5">Quick Actions</Typography>
          <CustomCarouselContainer>
            {ctaCards.map((card) => (
              <CtaCard
                key={card.title}
                title={card.title}
                subtitle={card.subtitle}
                subtitleColor={card.subtitleColor}
                background={card.background}
                color={card.color}
                buttonColor={card.buttonColor}
                buttonText={card.buttonText}
                image={card.image}
                imageWidth={card.imageWidth}
                imageHeight={card.imageHeight}
                onClick={card.onClick}
              />
            ))}
          </CustomCarouselContainer>
        </HubSectionContainer>
      )}
      {!expired && (
        <HubSectionContainer backgroundContrast wide sx={{ pb: 3.5 }}>
          <TrainerContactBanner />
        </HubSectionContainer>
      )}
      <HubSectionContainer backgroundContrast wide sx={{ pb: 4 }}>
        <TrainingPlan
          data={checklist}
          stats={stats}
          checked={checked}
          expanded={expanded}
          onCheck={onCheck}
          onExpand={onExpand}
          ageBracket={ageBracket}
          region={region}
          registerTaskRef={registerTaskRef}
        />
      </HubSectionContainer>
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={clearToast}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </Box>
  );
}

/**
 * /hub/home switch: fosters at a Plus AWO get the foster checklist (ticket 2538),
 * pre-surrender animals at a Plus AWO the pre-surrender experience (ticket 2530);
 * everyone else the 30-Day Checklist. Split into separate components so each keeps
 * a stable hook order.
 */
function Home() {
  const { petParent, loading } = useHubPetParent();
  if (loading) return null;
  const audience = getHubAudience(petParent);
  const fosterAnimal = audience === 'foster' ? getFosterAnimal(petParent) : null;
  if (fosterAnimal) return <FosterHome animal={fosterAnimal} />;
  if (audience === 'preSurrender') {
    const animal = getPreSurrenderAnimal(petParent);
    if (animal) return <PreSurrenderHome animal={animal} />;
  }
  return <AdopterHome />;
}

export default Home;
